import React, { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import { contractsService } from '../../../services/firestore';
import { companyService } from '../../../services/companyService';
import Button from '../../../ui/Button';
import IconButton from '../../../ui/IconButton';
import Card from '../../../ui/Card';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../../ui/Table';
import { Field, Input, Select, FormGrid } from '../../../ui/Field/Field';
import Dialog from '../../../ui/Dialog/Dialog';
import { toast } from '../../../ui/feedback';
import LoadingSpinner from '../../../components/LoadingSpinner';
import logger from '../../../utils/logger';
import InvoiceNumberField, { useInvoiceNumber } from '../../../components/InvoiceNumberField';
import { INVOICE_PREFIX, invoiceCounterOn } from '../../../services/invoiceNumbers';
import { useAuth } from '../../../contexts/AuthContext';
import { useCompanySettings } from '../../../services/companySettings';
import { drawInvoiceTable, ensureSpace, letterheadPage } from './pdfTable';
import { dubaiToday } from './invoiceDates';
import { CATEGORIES, categoryOf, newItem, withCategory, corporateLine, lineDescription, corporateTotals } from './corporateInvoice';

const CorporateGenerator = () => {
    const { currentUser } = useAuth();
    const { settings: companySettings } = useCompanySettings();   // our TRN (DUP-15): one owner, Administration
    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [invoiceDate, setInvoiceDate] = useState(dubaiToday());   // the Dubai day, not the UTC one
    const [showRefModal, setShowRefModal] = useState(false);
    // numbering while the shared counter is off (as before): ALWFQ-<typed>, nothing typed = the last 6 digits of the time
    const number = useInvoiceNumber(showRefModal, 0, { prefix: INVOICE_PREFIX, blank: () => Date.now().toString().slice(-6) });
    const [items, setItems] = useState(() => [newItem('rent')]);
    const [loading, setLoading] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    useEffect(() => {
        const fetchInitial = async () => {
            const data = await companyService.getAllCompanies();
            setCompanies(data);
            setLoading(false);
        };
        fetchInitial();
    }, []);

    const handleSearch = async () => {
        if (!searchTerm) return;
        setIsSearching(true);
        try {
            const results = await contractsService.searchContracts(searchTerm);
            if (results && results.length > 0) {
                const res = results[0];
                const desc = `${res.model || ''} - ${res.plate_number || ''} (${res.customer || ''})`;
                // Update the last empty item or add new
                setItems(prev => {
                    const last = prev[prev.length - 1];
                    if (!last.description && !last.amount) {
                        return [...prev.slice(0, -1), { ...last, description: desc }];
                    }
                    return [...prev, newItem('rent', { description: desc })];
                });
            }
        } catch (error) {
            logger.error(error);
        } finally {
            setIsSearching(false);
        }
    };

    const addItem = () => {
        setItems(prev => [...prev, newItem('rent')]);
    };

    const removeItem = (id) => {
        if (items.length > 1) {
            setItems(prev => prev.filter(item => item.id !== id));
        }
    };

    const updateItem = (id, field, value) => {
        setItems(prev => prev.map(item => {
            if (item.id !== id) return item;
            // the VAT tick box and the trips follow the category (each category has its own VAT rule)
            return field === 'category' ? withCategory(item, value) : { ...item, [field]: value };
        }));
    };

    // Subtotal / VAT / Total = the sums of the rounded lines (FIN-15)
    const totals = corporateTotals(items);

    const confirmAndGenerate = async () => {
        if (!selectedCompany) {
            toast.error('Please select a company first.');
            return;
        }
        let refNo;
        try {
            [refNo] = await number.claim(1, currentUser?.email || '');
        } catch {
            return; // the dialog shows why
        }
        setShowRefModal(false);
        generatePDF(refNo);
    };

    const generatePDF = async (refNo) => {
        if (!selectedCompany) {
            toast.error('Please select a company first.');
            return;
        }
        setIsGenerating(true);

        try {
            // Load the background letterhead image
            const response = await fetch('/Invoice.jpg');
            const imageBuffer = await response.arrayBuffer();
            const imgData = new Uint8Array(imageBuffer);

            const doc = new jsPDF();
            const pageWidth = 210;
            const pageHeight = 297;
            const margin = 15;
            let y = 35; // Start below the letterhead header

            // Add background image (Branding)
            doc.addImage(imgData, 'JPEG', 0, 0, pageWidth, pageHeight);

            // Header: TAX INVOICE
            doc.setFontSize(26);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0, 0, 0);
            doc.text('Tax Invoice', pageWidth / 2, y, { align: 'center' });
            y += 15;

            // Date and Ref (ALWFQ Prefix)
            doc.setFontSize(11);
            doc.setFont('helvetica', 'normal');
            const refText = `Ref: ${refNo}`;
            
            doc.text(`Date: ${new Date(invoiceDate).toLocaleDateString('en-GB')}`, margin, y);
            const refWidth = doc.getTextWidth(refText);
            doc.text(refText, pageWidth - margin - refWidth, y);
            y += 7;

            // TRN (Seller)
            doc.text(`TRN#: ${companySettings.trn}`, margin, y);
            y += 8;

            // Customer Info (Selected Company)
            const company = companies.find(c => c.id === selectedCompany);
            doc.setFont('helvetica', 'bold');
            doc.text(company.name, margin, y);
            y += 5;
            const addressLines = doc.splitTextToSize(company.address || '', 100);
            doc.text(addressLines, margin, y);
            y += (addressLines.length * 5);
            doc.text(`TRN#: ${company.trn || 'N/A'}`, margin, y);
            y += 7;

            // Subject
            doc.setFont('helvetica', 'bold');
            doc.text('SUB: Corporate Billing Services', margin, y);
            y += 7;

            doc.setFont('helvetica', 'normal');
            doc.text('Dear Sir,', margin, y);
            y += 5;
            doc.text('We thank you for your business. Please find below the details of the services rendered:', margin, y, { maxWidth: pageWidth - margin * 2 });
            y += 8;

            // Table: one row per item, header repeated on each page, every cell centred, and the
            // grand total kept on the page with the last item (drawInvoiceTable).
            const newPage = letterheadPage(doc, imgData);
            const rows = items.map((item, index) => {
                const line = corporateLine(item);
                return [String(index + 1), lineDescription(item), line.net.toFixed(2), line.vat.toFixed(2), line.total.toFixed(2)];
            });
            y = drawInvoiceTable(doc, {
                x: margin,
                y: y - 5,
                columns: [{ title: 'No.', width: 10 }, { title: 'Description', width: 75 }, { title: 'Net Amount', width: 32 },
                          { title: 'VAT (5%)', width: 28 }, { title: 'Total', width: 35 }],
                rows,
                total: [
                    { label: 'SUBTOTAL (AED):', value: totals.net.toFixed(2), labelFrom: 0 },
                    { label: 'VAT 5% (AED):', value: totals.vat.toFixed(2), labelFrom: 0 },
                    { label: 'GRAND TOTAL (AED):', value: totals.total.toFixed(2), labelFrom: 0 },
                ],
                newPage,
            });
            // the conditions and signature below need about 65 mm: never past the page
            y = ensureSpace(y + 13, 65, newPage);

            // Footer / Conditions
            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.text('General Conditions:', margin, y);
            doc.line(margin, y + 1, margin + 35, y + 1);
            y += 10;
            doc.setFont('helvetica', 'normal');
            doc.text('Terms of Payment: within 7 days', margin, y);
            y += 15;
            doc.text('Thanking you and assuring you of our best co-operation at all times.', margin, y);
            y += 15;
            doc.text('Best Regards,', margin, y);
            y += 10;
            doc.setFont('helvetica', 'bold');
            doc.text('Saudian Alwefaq Rent A Car', margin, y);

            doc.save(`Invoice_Corporate_${company.name.replace(/\s+/g, '_')}_${refNo}.pdf`);
        } catch (err) {
            logger.error(err);
            toast.error('Failed to generate PDF. Check console for details.');
        } finally {
            setIsGenerating(false);
        }
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div className="space-y-6">
            <h2 className="text-base font-semibold text-gray-900">Unified Corporate Invoice Generator</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Selection & Meta */}
                <div className="lg:col-span-2 space-y-4">
                    <Card>
                    <div className="space-y-4">
                        <FormGrid cols={2}>
                            <Field label="Select Company">
                                <Select
                                    value={selectedCompany || ''}
                                    onChange={(e) => setSelectedCompany(e.target.value)}
                                    placeholder="-- Choose Company --"
                                    options={companies.map(c => ({ value: c.id, label: c.name }))}
                                />
                            </Field>
                            <Field label="Invoice Date">
                                <Input
                                    type="date"
                                    value={invoiceDate}
                                    onChange={(e) => setInvoiceDate(e.target.value)}
                                />
                            </Field>
                        </FormGrid>
                        <Field label="Smart Search (Contract/Plate)">
                            <Input
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                placeholder="Enter Plate or Contract"
                                suffix={(
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        iconOnly
                                        aria-label="Search"
                                        onClick={handleSearch}
                                        isLoading={isSearching}
                                        leadingIcon={<Icon glyph={ICONS.search} size="sm" />}
                                    />
                                )}
                            />
                        </Field>
                    </div>
                    </Card>

                    {/* Items */}
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeader className="w-40">Type</TableHeader>
                                    <TableHeader>Description</TableHeader>
                                    <TableHeader className="w-20">Trips</TableHeader>
                                    <TableHeader className="w-20">VAT</TableHeader>
                                    <TableHeader className="w-32">Amount</TableHeader>
                                    <TableHeader className="w-28">Line Total</TableHeader>
                                    <TableHeader className="w-12"><span className="sr-only">Remove</span></TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((item, index) => {
                                    const rule = categoryOf(item.category).rule;
                                    const line = corporateLine(item);
                                    return (
                                        <TableRow key={item.id}>
                                            <TableCell>
                                                <Select
                                                    size="sm"
                                                    aria-label={`Item ${index + 1} type`}
                                                    value={item.category}
                                                    onChange={(e) => updateItem(item.id, 'category', e.target.value)}
                                                    options={CATEGORIES.map(cat => ({ value: cat.id, label: cat.label }))}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Input
                                                    size="sm"
                                                    aria-label={`Item ${index + 1} description`}
                                                    value={item.description}
                                                    onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                                                    placeholder="Vehicle, Period, etc."
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {rule === 'salik' ? (
                                                    <Input
                                                        size="sm"
                                                        type="number"
                                                        aria-label={`Item ${index + 1} Salik trips`}
                                                        value={item.trips}
                                                        onChange={(e) => updateItem(item.id, 'trips', e.target.value)}
                                                        placeholder="1"
                                                    />
                                                ) : <span className="text-gray-400">—</span>}
                                            </TableCell>
                                            <TableCell>
                                                {rule === 'vat' ? (
                                                    <input
                                                        type="checkbox"
                                                        aria-label={`Item ${index + 1} includes VAT`}
                                                        checked={item.includeVAT}
                                                        onChange={(e) => updateItem(item.id, 'includeVAT', e.target.checked)}
                                                        className="w-4 h-4"
                                                        style={{ accentColor: 'var(--accent)' }}
                                                    />
                                                ) : (
                                                    <span className="text-xs text-gray-600" title={rule === 'fine' ? '5% service fee + VAT 5% on the fee only' : 'The tolls hold their VAT; AED 1.05 a trip (AED 1 + VAT) is added'}>
                                                        {rule === 'fine' ? 'On fee' : 'Inside'}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Input
                                                    size="sm"
                                                    type="number"
                                                    aria-label={`Item ${index + 1} amount`}
                                                    value={item.amount}
                                                    onChange={(e) => updateItem(item.id, 'amount', e.target.value)}
                                                    placeholder={rule === 'fine' ? 'Fine' : rule === 'salik' ? 'Tolls' : '0.00'}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <span className="ds-cell-num" title={line.note || undefined}>{line.total.toFixed(2)}</span>
                                            </TableCell>
                                            <TableCell>
                                                <IconButton size="sm" icon={ICONS.close} label="Remove item" onClick={() => removeItem(item.id)} />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                        <div className="p-3 border-t border-gray-200 flex flex-wrap items-center justify-between gap-2">
                            <Button size="sm" variant="secondary" onClick={addItem} leadingIcon={<Icon glyph={ICONS.plus} size="sm" />}>
                                Add Item
                            </Button>
                            <div className="text-right">
                                <div className="text-xs text-gray-500">Subtotal</div>
                                <div className="text-base font-semibold text-gray-900 tabular-nums">AED {totals.net.toFixed(2)}</div>
                            </div>
                        </div>
                    </TableContainer>
                    <p className="text-xs text-gray-500 m-0">
                        Traffic / Parking fine: type the fine - the 5% service fee and VAT 5% on the fee are added. Salik: type the tolls (VAT already inside) and the trips - AED 1.05 a trip (AED 1 + its VAT) is added.
                    </p>
                </div>

                {/* Summary & Action */}
                <div className="space-y-4">
                    <Card
                        className="lg:sticky lg:top-6"
                        title={(
                            <span className="flex items-center gap-2">
                                <Icon glyph={ICONS.invoices} size="sm" /> Invoice Summary
                            </span>
                        )}
                    >
                        <dl className="space-y-2 mb-6 text-sm">
                            <div className="flex justify-between text-gray-600">
                                <dt>Subtotal</dt>
                                <dd className="tabular-nums">AED {totals.net.toFixed(2)}</dd>
                            </div>
                            <div className="flex justify-between text-gray-600">
                                <dt>VAT (5%)</dt>
                                <dd className="tabular-nums">AED {totals.vat.toFixed(2)}</dd>
                            </div>
                            <div className="pt-2 border-t border-gray-200 flex justify-between text-lg font-semibold text-gray-900">
                                <dt>Total</dt>
                                <dd className="tabular-nums">AED {totals.total.toFixed(2)}</dd>
                            </div>
                        </dl>

                        <Button
                            variant="primary"
                            size="lg"
                            fullWidth
                            leadingIcon={<Icon glyph={ICONS.download} size="sm" />}
                            onClick={() => setShowRefModal(true)}
                            disabled={isGenerating || items.length === 0}
                            isLoading={isGenerating}
                        >
                            {isGenerating ? 'Generating...' : 'Generate Invoice PDF'}
                        </Button>
                    </Card>
                </div>
            </div>

            {/* Invoice number (the one invoice-number service) */}
            <Dialog
                open={showRefModal}
                onClose={() => setShowRefModal(false)}
                title={invoiceCounterOn() ? 'Invoice number' : 'Invoice Reference'}
                description={invoiceCounterOn() ? 'The next free number of the ALWFQ- series is filled in.' : "Enter the reference number. Prefix 'ALWFQ-' is automatic."}
                size="sm"
                footer={(
                    <>
                        <Button onClick={() => setShowRefModal(false)} variant="secondary">
                            Cancel
                        </Button>
                        <Button onClick={confirmAndGenerate} variant="primary">
                            Generate
                        </Button>
                    </>
                )}
            >
                <InvoiceNumberField state={number} placeholder="e.g. 1001" />
            </Dialog>
        </div>
    );
};

export default CorporateGenerator;
